import type { ReactNode } from "react";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import { cx } from "@/lib/cx";
import { Spinner } from "./Spinner";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "accent";
export type ButtonSize = "sm" | "md" | "lg";

const base =
  "press transition-ui relative inline-flex shrink-0 items-center justify-center gap-1.5 rounded-control font-medium " +
  "select-none whitespace-nowrap data-disabled:opacity-45 data-disabled:pointer-events-none data-pending:cursor-progress";

/**
 * primary   monochrome (graphite in light, near-white in dark). One per view: the main action.
 * secondary hairline-bordered surface. Most actions.
 * ghost     no chrome until hover. Toolbars, inline and tertiary actions.
 * danger    destructive confirmation only.
 * accent    rare: a call to action that must read as "new" or "connect". Never next to a primary.
 */
const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-primary text-primary-fg shadow-[inset_0_1px_0_var(--highlight),0_1px_2px_var(--shadow-soft)] " +
    "data-hovered:bg-primary-hover data-pressed:bg-primary-active",
  secondary:
    "border border-line bg-surface text-fg shadow-[0_1px_2px_var(--shadow-soft)] " +
    "data-hovered:bg-hover data-hovered:border-line-strong data-pressed:bg-active",
  ghost: "text-fg-muted data-hovered:bg-hover data-hovered:text-fg data-pressed:bg-active data-pressed:text-fg",
  danger: "bg-danger text-danger-fg data-hovered:bg-danger-hover data-pressed:bg-danger-hover",
  accent: "bg-accent text-accent-fg data-hovered:bg-accent-hover data-pressed:bg-accent-active",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-(--control-h-sm) min-w-(--control-h-sm) px-2 text-xs",
  md: "h-(--control-h-md) min-w-(--control-h-md) px-3 text-sm",
  lg: "h-(--control-h-lg) min-w-(--control-h-lg) px-3.5 text-sm",
};

const iconSizes: Record<ButtonSize, string> = {
  sm: "size-(--control-h-sm) text-xs",
  md: "size-(--control-h-md) text-sm",
  lg: "size-(--control-h-lg) text-sm",
};

/** Class string for anything that must look like a button (e.g. a router Link). */
export function buttonStyles({
  variant = "secondary",
  size = "md",
  iconOnly,
  className,
}: {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Square, padding-free sizing for IconButton. */
  iconOnly?: boolean;
  className?: string;
} = {}) {
  return cx(base, variants[variant], iconOnly ? iconSizes[size] : sizes[size], className);
}

export interface ButtonProps extends Omit<AriaButtonProps, "className" | "children"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  children?: ReactNode;
}

/**
 * Text button. Width follows content (German runs ~30% longer), so never set a fixed width.
 * Icon-only buttons must use IconButton, which requires an aria-label.
 */
export function Button({ variant = "secondary", size = "md", className, children, ...props }: ButtonProps) {
  return (
    <AriaButton className={buttonStyles({ variant, size, className })} {...props}>
      {({ isPending }) => (
        <>
          {isPending ? <Spinner /> : null}
          {children}
        </>
      )}
    </AriaButton>
  );
}
